import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Apple, ArrowLeft, Play } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { trackCampaignEvent, warmKnownLocation } from '../utils/campaignAnalytics';
import { revokeMetaPixelConsent, trackMetaLandingView } from '../utils/metaPixel';
import { getMarketingConsent, setMarketingConsent } from '../utils/marketingConsent';
import './BeestmodeCampaignLandingPage.css';

const APP_STORE_URL = 'https://apps.apple.com/il/app/beestmode/id6745525936';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.hagidevstudios.beestmode';
const CAMPAIGN_MESSAGE = 'האימון שלך. הקצב שלך. התוצאות שלך.';

// Campaign codes are matched from the `banner` query parameter. Unknown codes
// always resolve to the default entry.
export const BANNER_VARIANTS = {
  default: {
    message: CAMPAIGN_MESSAGE,
    downloadCta: 'הורידו מהחנות עכשיו:',
    image: '/Beestmode/monster-landing.png',
    imageAlt: 'הקמע הכחול של Beestmode',
    accent: '#c8ff45',
    glow: 'rgba(200, 255, 69, 0.28)',
  },
  banner_men_1: {
    message: CAMPAIGN_MESSAGE,
    downloadCta: 'הורד מהחנות עכשיו:',
    image: '/Beestmode/monster-landing.png',
    imageAlt: 'הקמע הגברי הכחול של Beestmode',
    accent: '#ffb347',
    glow: 'rgba(255, 179, 71, 0.28)',
  },
  banner_women_1: {
    message: CAMPAIGN_MESSAGE,
    downloadCta: 'הורידי מהחנות עכשיו:',
    image: '/Beestmode/landing_page_monster_fem_purple.png',
    imageAlt: 'הקמע הנשי הכחול של Beestmode',
    accent: '#880bdf',
    accentInk: '#ffffff',
    glow: 'rgba(136, 11, 223, 0.27)',
  },
  banner_unisex_1: {
    message: CAMPAIGN_MESSAGE,
    downloadCta: 'הורידו מהחנות עכשיו:',
    image: '/Beestmode/monster-landing.png',
    imageAlt: 'הקמע הכחול של Beestmode',
    accent: '#c6a7ff',
    glow: 'rgba(198, 167, 255, 0.26)',
  },
};

export const detectDevicePlatform = (
  browserNavigator = typeof navigator !== 'undefined' ? navigator : undefined,
) => {
  const userAgent = browserNavigator?.userAgent || '';
  const platform = browserNavigator?.userAgentData?.platform || browserNavigator?.platform || '';

  if (/android/i.test(userAgent) || /android/i.test(platform)) return 'android';

  if (
    /iPhone|iPad|iPod/i.test(userAgent)
    || /iPhone|iPad|iPod/i.test(platform)
    || (platform === 'MacIntel' && (browserNavigator?.maxTouchPoints || 0) > 1)
  ) {
    return 'ios';
  }

  return 'unknown';
};

export const getBannerVariant = (requestedCode) => {
  const normalizedCode = (requestedCode || '').trim().toLowerCase();
  const isValid = normalizedCode !== '' && Object.hasOwn(BANNER_VARIANTS, normalizedCode);

  return {
    code: isValid ? normalizedCode : 'default',
    isValid,
    requestedCode: normalizedCode.slice(0, 64),
    variant: BANNER_VARIANTS[isValid ? normalizedCode : 'default'],
  };
};

const BeestmodeCampaignLandingPage = () => {
  const location = useLocation();
  const trackedPage = useRef('');
  const [marketingConsent, setMarketingConsentState] = useState(() => getMarketingConsent());
  const [showConsentPreferences, setShowConsentPreferences] = useState(
    () => getMarketingConsent() === 'unknown',
  );
  const devicePlatform = useMemo(() => detectDevicePlatform(), []);
  const banner = useMemo(() => {
    const requestedCode = new URLSearchParams(location.search).get('banner');
    return getBannerVariant(requestedCode);
  }, [location.search]);

  const analyticsContext = useMemo(() => ({
    bannerCode: banner.code,
    requestedBannerCode: banner.requestedCode || null,
    isKnownBannerCode: banner.isValid,
    detectedPlatform: devicePlatform,
  }), [banner.code, banner.isValid, banner.requestedCode, devicePlatform]);

  useEffect(() => {
    document.documentElement.classList.add('campaign-page-lock');
    document.body.classList.add('campaign-page-lock');
    warmKnownLocation();

    return () => {
      document.documentElement.classList.remove('campaign-page-lock');
      document.body.classList.remove('campaign-page-lock');
    };
  }, []);

  useEffect(() => {
    const pageKey = `${location.pathname}${location.search}`;
    if (trackedPage.current === pageKey) return;

    trackedPage.current = pageKey;
    trackCampaignEvent('campaign_page_view', analyticsContext);
  }, [analyticsContext, location.pathname, location.search]);

  useEffect(() => {
    if (marketingConsent !== 'granted') return;

    const pageKey = `${location.pathname}${location.search}`;
    trackMetaLandingView({ pageKey, ...analyticsContext });
  }, [analyticsContext, location.pathname, location.search, marketingConsent]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Beestmode | האימון שלך מתחיל כאן';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const trackStoreClick = (store) => {
    trackCampaignEvent('campaign_store_click', {
      ...analyticsContext,
      store,
      isRecommendedStore: devicePlatform === store,
    });
  };

  const handleConsentChoice = (status) => {
    setMarketingConsent(status);
    setMarketingConsentState(status);
    setShowConsentPreferences(false);

    if (status === 'denied') {
      revokeMetaPixelConsent();
    }

    trackCampaignEvent('campaign_marketing_consent_updated', {
      ...analyticsContext,
      marketingConsent: status,
    });
  };

  const handleOpenConsentPreferences = () => {
    setShowConsentPreferences(true);
    trackCampaignEvent('campaign_marketing_preferences_opened', analyticsContext);
  };

  const handlePrivacyLink = () => {
    trackCampaignEvent('campaign_privacy_policy_opened', analyticsContext);
  };

  const appStoreEmphasis = devicePlatform === 'ios' ? 'recommended' : devicePlatform === 'unknown' ? 'neutral' : 'secondary';
  const playStoreEmphasis = devicePlatform === 'android' ? 'recommended' : devicePlatform === 'unknown' ? 'neutral' : 'secondary';

  return (
    <main
      className={`campaign-landing${showConsentPreferences ? ' campaign-landing--consent-visible' : ''}`}
      dir="rtl"
      style={{
        '--campaign-accent': banner.variant.accent,
        '--campaign-accent-ink': banner.variant.accentInk || '#080a09',
        '--campaign-glow': banner.variant.glow,
      }}
      data-banner-code={banner.code}
      data-device-platform={devicePlatform}
    >
      <div className="campaign-landing__ambient" aria-hidden="true" />

      <section className="campaign-landing__layout" aria-labelledby="campaign-title">
        <div className="campaign-visual">
          <div className="campaign-visual__halo" />
          <img
            className="campaign-visual__monster"
            src={banner.variant.image}
            alt={banner.variant.imageAlt}
          />
        </div>

        <div className="campaign-content">
          <div className="campaign-brand" aria-label="Beestmode">
            <img
              className="campaign-brand__mark"
              src="/logo_shade_transparent.png"
              alt=""
              aria-hidden="true"
            />
            <span>BEESTMODE</span>
          </div>

          <h1 id="campaign-title">{banner.variant.message}</h1>

          <p className="campaign-download-cta">{banner.variant.downloadCta}</p>

          <div className="campaign-store-links" aria-label="הורדת האפליקציה">
            <a
              className={`campaign-store-link campaign-store-link--${appStoreEmphasis}`}
              href={APP_STORE_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackStoreClick('ios')}
              aria-label="הורדת Beestmode מ-App Store"
            >
              <Apple aria-hidden="true" size={28} strokeWidth={2.2} />
              <span className="campaign-store-link__copy">
                {appStoreEmphasis === 'recommended' && <small>מומלץ למכשיר הזה</small>}
                <strong>App Store</strong>
              </span>
              <ArrowLeft className="campaign-store-link__arrow" aria-hidden="true" size={19} />
            </a>

            <a
              className={`campaign-store-link campaign-store-link--${playStoreEmphasis}`}
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackStoreClick('android')}
              aria-label="הורדת Beestmode מ-Google Play"
            >
              <Play aria-hidden="true" size={27} strokeWidth={2.2} fill="currentColor" />
              <span className="campaign-store-link__copy">
                {playStoreEmphasis === 'recommended' && <small>מומלץ למכשיר הזה</small>}
                <strong>Google Play</strong>
              </span>
              <ArrowLeft className="campaign-store-link__arrow" aria-hidden="true" size={19} />
            </a>
          </div>

          <div className="campaign-privacy-controls">
            <a
              href="/beestmodeprivacy#cookies"
              target="_blank"
              rel="noreferrer"
              onClick={handlePrivacyLink}
            >
              מדיניות פרטיות
            </a>
            <span aria-hidden="true">•</span>
            <button type="button" onClick={handleOpenConsentPreferences}>
              הגדרות עוגיות
            </button>
          </div>
        </div>
      </section>

      {showConsentPreferences && (
        <aside className="campaign-consent" role="region" aria-label="העדפות עוגיות שיווק">
          <p>
            אנחנו משתמשים בעוגיות שיווק של Meta למדידת ביקורים ולהצגת פרסום מותאם.
            הבחירה לא תשפיע על הורדת האפליקציה.{' '}
            <a
              href="/beestmodeprivacy#cookies"
              target="_blank"
              rel="noreferrer"
              onClick={handlePrivacyLink}
            >
              מידע נוסף
            </a>
          </p>
          <div className="campaign-consent__actions">
            <button
              className="campaign-consent__reject"
              type="button"
              onClick={() => handleConsentChoice('denied')}
            >
              דחייה
            </button>
            <button
              className="campaign-consent__accept"
              type="button"
              onClick={() => handleConsentChoice('granted')}
            >
              אישור
            </button>
          </div>
        </aside>
      )}
    </main>
  );
};

export default BeestmodeCampaignLandingPage;
